import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `news.articles.precio-dinamico-restaurantes-coste-vs-demanda.${f}`

const REF = {
    elespanol: {
        href: 'https://www.elespanol.com/cocinillas/actualidad-gastronomica/20260420/dani-garcia-anos-chef-precio-viaje-cambia-demanda-debe-cambiar-restaurante/1003744214085_0.html',
        domain: 'elespanol.com',
        label: 'El Español',
    },
    theobjective: {
        href: 'https://theobjective.com/gastronomia/2026-04-21/precios-dinamicos-restaurantes-dani-garcia/',
        domain: 'theobjective.com',
        label: 'The Objective',
    },
    comunidadMadrid: {
        href: 'https://www.comunidad.madrid/consumo/informacion-completa-restaurantes',
        domain: 'comunidad.madrid',
        label: 'Comunidad de Madrid',
    },
    ocu: {
        href: 'https://www.infobae.com/espana/2026/07/14/el-detalle-en-el-que-debes-fijarte-al-pedir-en-un-restaurante-este-verano-y-que-es-ilegal-segun-la-ocu/',
        domain: 'infobae.com',
        label: 'OCU · Infobae',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

function TipiLink({ children }: { children?: ReactNode }) {
    const { t } = useTranslation()
    return (
        <LocaleLink to='/' className={article.link}>
            {children ?? t(k('tipiLink'), 'Tipi')}
        </LocaleLink>
    )
}

export function PrecioDinamicoCosteArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo del precio dinámico por coste y me gustaría ver qué dice el margen de mi carta.',
        ),
    )

    const weekActions: ReactNode[] = [
        <>
            {t(
                k('weekActions.1'),
                'Revisa qué platos tienen mayor peso de un solo ingrediente volátil, temporada, pescado, carne: son los candidatos al ajuste, no toda la carta.',
            )}
        </>,
        <>
            {t(
                k('weekActions.2'),
                'Fija un umbral: variación superior al 5% en un ingrediente que pese más del 10% del coste del plato.',
            )}
        </>,
        <>
            {t(
                k('weekActions.3'),
                'Actualiza el escandallo al cruzar el umbral y cambia carta física y QR el mismo día.',
            )}
        </>,
        <>
            {t(
                k('weekActions.4'),
                'Deja una frase que el camarero pueda repetir: "+1€ por la subida de la alcachofa de temporada, mismo margen de siempre".',
            )}
        </>,
        <>{t(k('weekActions.5'), 'Empieza por guarniciones, no por el plato ancla de la casa.')}</>,
    ]

    const sources = [
        {
            label: t(
                k('sources.1'),
                'Dani García, 50 años, chef: "Si el precio de un viaje cambia según la demanda, también debe cambiar el de un restaurante", El Español',
            ),
            href: REF.elespanol.href,
        },
        {
            label: t(
                k('sources.2'),
                'Precios dinámicos en los restaurantes: ¿pagaremos más por cenar un sábado?, The Objective',
            ),
            href: REF.theobjective.href,
        },
        {
            label: t(k('sources.3'), 'Información completa: Restaurantes, Comunidad de Madrid · Consumo'),
            href: REF.comunidadMadrid.href,
        },
        {
            label: t(
                k('sources.4'),
                'El detalle en el que debes fijarte al pedir en un restaurante este verano y que es ilegal, según la OCU, Infobae (14/07/2026)',
            ),
            href: REF.ocu.href,
        },
        {
            label: t(
                k('sources.5'),
                'Benchmark de márgenes de escandallo (27-32% coste materia prima / 65-75% margen bruto): estándar de referencia usado por consultoras de food cost hostelería, no dato propio de Tipi.',
            ),
        },
        {
            label: t(k('sources.anuario'), 'Anuario de la Hostelería de España (más de 300.000 establecimientos)'),
            href: REF.anuario.href,
        },
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(
                                k('dek.before'),
                                'Dani García propuso cobrar más el sábado. Te explicamos por qué el precio dinámico que sí funciona en tu restaurante es otro: el que sigue el ',
                            )}
                            <strong>{t(k('dek.strong'), 'coste real')}</strong>
                            {t(k('dek.after'), '.')}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(k('heroAlt'), 'Alcachofas frescas sobre una encimera de acero en cocina profesional')}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <div className={article.proseIntro}>
                    <p className={article.p}>
                        <span className={article.cite}>
                            {t(
                                k('intro.1.before'),
                                'En abril, Dani García propuso cobrar más caro un sábado a las 10 de la noche y más barato un lunes a las 7, como hacen las aerolíneas.',
                            )}
                            <SourceChip source='elespanol' />
                        </span>{' '}
                        {t(k('intro.1.mid'), 'Desde ')}
                        <TipiLink />
                        {t(
                            k('intro.1.after'),
                            ' creemos que hay otro precio dinámico en restaurantes que sí tiene sentido: el que sube o baja cuando el coste real del plato, una verdura de temporada, un pescado, una pieza de carne, se mueve más de un 5%, la misma lógica que el sector ya aplica al marisco bajo la etiqueta PSM.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('intro.2'),
                            '¿Quién no ha visto subir la alcachofa a final de temporada y se ha preguntado si tocar el precio del plato que en noviembre compraba a un tercio de lo que cuesta ahora? Lo piensas, miras la carta y al final lo dejas. Total, quedan pocas semanas. El problema es que esas pocas semanas se repiten cada año, con la alcachofa y media docena de productos más, y el margen que se come nunca aparece como una sola cifra: se diluye plato a plato, semana a semana.',
                        )}
                    </p>
                </div>

                <section id='demanda-o-coste'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.demandaOCoste'), '¿Precio dinámico por demanda o por coste? No es lo mismo')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <span className={article.cite}>
                                {t(
                                    k('demanda.p1'),
                                    'Dani García no habla de esto. Habla de otra cosa, y conviene no mezclarlas.',
                                )}
                                <SourceChip source='theobjective' />
                            </span>
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('demanda.p2.strong'), 'El precio dinámico por demanda')}</strong>
                            {t(
                                k('demanda.p2.after'),
                                ' es el que proponía él: subir un plato el sábado a las 10 porque el local está lleno, bajarlo el lunes porque está vacío. Es la lógica de una aerolínea.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('demanda.p3.strong'), 'El precio dinámico por coste')}</strong>
                            {t(
                                k('demanda.p3.after'),
                                ' es el que planteamos aquí: subir o bajar el PVP porque cambió lo que cuesta producirlo, no porque haya más o menos gente en el local. Es la lógica del PSM, Precio Según Mercado, la fórmula que ya usan bares y restaurantes de toda España en pescado y marisco fresco.',
                            )}
                        </p>
                    </div>
                    <div className={article.tableWrap}>
                        <table className={article.table}>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.model.col1'), 'Modelo')}</th>
                                    <th scope='col'>{t(k('table.model.col2'), 'Qué mueve el precio')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{t(k('table.model.row1.model'), 'Por demanda (Dani García)')}</td>
                                    <td>{t(k('table.model.row1.move'), 'La ocupación del local a esa hora')}</td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.model.row2.model'), 'Por coste (esta propuesta)')}</td>
                                    <td>
                                        {t(
                                            k('table.model.row2.move'),
                                            'El coste real de la materia prima en el escandallo',
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('demanda.p4'),
                                'Un cliente que paga más porque el local está lleno paga, en el fondo, por algo que no le beneficia a él. Un cliente que paga más porque el aceite subió está pagando lo mismo que le costaría cocinarlo en casa: más.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='es-legal'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.esLegal'), '¿Es legal cambiar el precio de un plato de una semana a otra en España?')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <span className={article.cite}>
                                {t(
                                    k('legal.p1'),
                                    'Sí, y aquí está el detalle que casi nadie cuenta. La Comunidad de Madrid recoge que si se pide un plato fuera de carta o marcado ',
                                )}
                                <strong>{t(k('legal.p1.strong'), 'S.M. (según mercado)')}</strong>
                                {t(
                                    k('legal.p1.after'),
                                    ', el establecimiento debe indicar el precio o facilitar una carta donde aparezca.',
                                )}
                                <SourceChip source='comunidadMadrid' />
                            </span>{' '}
                            <span className={article.cite}>
                                {t(
                                    k('legal.p2'),
                                    'La OCU lo confirmó en julio: para cualquier producto según mercado debe existir una referencia suficiente para que el consumidor sepa cuánto va a pagar.',
                                )}
                                <SourceChip source='ocu' />
                            </span>{' '}
                            {t(k('legal.p3'), 'Traducido: puedes mover el precio, pero no dejarlo en el aire.')}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <span className={article.cite}>
                                {t(
                                    k('legal.p4'),
                                    'Y si tu carta es solo QR, la OCU recuerda que los restaurantes deben disponer también de una carta física. Si el QR dice 9€ y la pizarra 8€, el problema es tuyo, no del cliente.',
                                )}
                                <SourceChip source='ocu' />
                            </span>
                        </p>
                    </div>
                </section>

                <aside className={article.breakout} aria-label={t(k('breakout.aria'), 'El precio que sigue al coste')}>
                    <div className={article.breakCopy}>
                        <p className={article.breakEyebrow}>
                            {t(k('breakout.eyebrow'), 'El precio que sigue al coste')}
                        </p>
                        <p className={article.breakLead}>
                            {t(
                                k('breakout.lead'),
                                'Misma lógica que el PSM del marisco: el PVP se mueve cuando se mueve la materia prima.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('breakout.p'),
                                'No porque el local esté lleno el sábado. Porque la alcachofa, el pescado o la carne ya no cuestan lo mismo.',
                            )}
                        </p>
                    </div>
                </aside>

                <section id='parte-no-tan-bonita'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.parteNoBonita'), 'La parte que no es tan bonita')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('feo.p1'),
                                'Para hacerlo bien necesitas trazabilidad de coste por proveedor, no trimestral, sino cada vez que un ingrediente clave cruce un umbral, y actualizar la carta física y el QR el mismo día. También explicar el cambio: una subida por coste se tolera mejor que una por demanda, pero solo si el cliente ve la justificación. Y hay un techo: nadie quiere pedir su plato de siempre y encontrarse un precio distinto cada vez que viene.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='cuenta-de-la-vieja'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.cuentaVieja'), 'Antes del dinámico: el escandallo quieto')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <span className={article.cite}>
                                {t(k('cuenta.p1.before'), 'España tiene ')}
                                <strong>
                                    {t(k('cuenta.p1.strong'), 'más de 300.000 establecimientos de hostelería')}
                                </strong>
                                {t(k('cuenta.p1.after'), ', según el Anuario de Hostelería de España.')}
                                <SourceChip source='anuario' />
                            </span>{' '}
                            <em>{t(k('cuenta.note'), 'Estimación propia:')}</em>{' '}
                            {t(
                                k('cuenta.p2'),
                                'la mayoría fija el precio de carta por intuición, copiando al de enfrente o heredando el número de hace tres años, no a partir de un escandallo actualizado. Antes de mover el precio con el coste, la ganancia fácil es tener el escandallo bien hecho y quieto. El dinámico por coste no es el primer paso. Es el segundo.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='ejemplo-alcachofa'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.ejemplo'), 'Un ejemplo con números: la alcachofa que se triplica de precio')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.pNote, article.pGapMd)}>
                            {t(k('ejemplo.note'), 'Ejemplo ilustrativo, con cifras de estimación propia.')}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('ejemplo.p1.before'),
                                'Escandallo de una ración de alcachofas salteadas con jamón: 400g de alcachofa, más jamón, aceite, ajo y limón. En noviembre, con la alcachofa a ',
                            )}
                            <strong>1,20€/kg</strong>
                            {t(k('ejemplo.p1.mid1'), ', el coste es ')}
                            <strong>1,68€</strong>
                            {t(k('ejemplo.p1.mid2'), '. PVP: ')}
                            <strong>6,50€</strong>
                            {t(k('ejemplo.p1.mid3'), '. Margen bruto: ')}
                            <strong>74%</strong>.
                        </p>
                        <p className={article.p}>
                            {t(k('ejemplo.p2.before'), 'A final de temporada, a ')}
                            <strong>3,60€/kg</strong>
                            {t(k('ejemplo.p2.mid1'), ', el triple, el coste sube a ')}
                            <strong>2,64€</strong>
                            {t(k('ejemplo.p2.mid2'), '. Si no mueves el PVP, el margen cae al ')}
                            <strong>59%</strong>
                            {t(
                                k('ejemplo.p2.after'),
                                ': quince puntos menos, en el mismo plato, sin que se note hasta que cierras el mes.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('ejemplo.p3.before'), 'Mantener el 74% exigiría subir a ')}
                            <strong>10,20€</strong>
                            {t(
                                k('ejemplo.p3.after'),
                                ', un salto del 57% que casi ningún cliente acepta de un día para otro. Lo que hace la mayoría, sin llamarlo dinámico, es subir a mitad de camino, a 7,50€, por ejemplo, o quitar el plato en semanas de escasez. El aprendizaje no es cobrar 10,20€: es que sin medir el coste por temporada no sabes que pierdes quince puntos de margen al precio de siempre.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='esta-semana'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Cómo empezar el precio por coste esta semana')}
                    </h2>
                    <div className={article.prose}>
                        <NewsArticleNumList items={weekActions} />
                    </div>
                </section>

                <section id='pendientes'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.pendientes'), 'Cuántas veces puedes cambiar un precio (y qué no sabemos)')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('pendientes.p1'),
                                'No hay normativa que fije cuántas veces puedes cambiar un precio; la ley exige visibilidad y coherencia entre lo mostrado y lo cobrado, pero no limita la frecuencia. No conocemos casos documentados en España de restaurantes de barrio aplicando repricing sistemático por coste fuera del pescado: esto es una propuesta razonada, no una práctica a escala. Y sigue siendo hipótesis que el cliente distinga de verdad entre "sube por coste, explicado" y "sube porque sí".',
                            )}
                        </p>
                    </div>
                </section>

                <div className={article.prose}>
                    <p className={article.p}>
                        {t(
                            k('closing.p1.before'),
                            'Llevar esta cuenta a mano, plato a plato, temporada a temporada, es un trabajo al alcance de muy pocos. Por eso aplicaciones como ',
                        )}
                        <TipiLink />
                        {t(
                            k('closing.p1.after'),
                            ' hacen ese seguimiento por ti: te avisan qué platos han perdido margen, cuánto subir el PVP o cómo arreglarlo sin tocarlo. Si quieres ver qué dice tu propia carta, escríbenos y le ponemos números.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('closing.p2'),
                            'Si sale algo nuevo sobre precios y costes, lo cuentas aquí antes que en ningún sitio.',
                        )}
                    </p>
                </div>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Qué platos de tu carta han perdido margen?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Revisamos contigo qué platos se han comido el margen por coste y qué haría falta mover, o no, en el PVP.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.label}>
                                {source.href ? (
                                    <a
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {source.label}
                                    </a>
                                ) : (
                                    <span>{source.label}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
